"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type ProcessStep = {
  number: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const steps: ProcessStep[] = [{
  number: "01.",
  title: "Initial Contact",
  description: "Our process begins with a detailed first contact to review your family's specific needs. We provide a clear timeline for the intake phase and identify required documents, including court orders or referral forms. During this stage, we conduct preliminary safety screenings and distinguish between court-ordered mandates and family-initiated requests to ensure the appropriate support structure is established.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_inquiry.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_inquiry.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_inquiry.png-wvc-sizes",
  alt: "Professional intake coordinator reviewing family support documentation in a calm office setting"
}, {
  number: "02.",
  title: "Application",
  description: "We perform a comprehensive review of all provided documentation and court records. Our team applies specific safety screening criteria to evaluate potential risks and ensure a child-centered approach. This assessment phase typically takes 3-5 business days, during which we process referral types and verify that all legal and safety requirements for neutral supervision are met.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_assessment.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_assessment.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_assessment.png-wvc-sizes",
  alt: "Neutral professional reviewing court-ordered visitation guidelines with focus and care"
}, {
  number: "03.",
  title: "Fee Structure & Court Documents",
  description: "Once the assessment is complete, we finalize all intake paperwork and develop a customized safety plan. We establish firm boundaries and expectations for all parties involved. For court-ordered cases, we align protocols with specific judicial directives, while family-initiated cases focus on mutually agreed-upon goals, ensuring a clear timeline for the commencement of services.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_planning.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_planning.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_planning.webp-wvc-sizes",
  alt: "Structured planning session outlining safe visitation protocols and family support goals"
}, {
  number: "04.",
  title: "Orientations & Agreements",
  description: "The start-up process involves a final documentation check before the first monitored session. We implement active safety monitoring protocols tailored to the referral type. Services commence according to the established schedule, with professional supervisors ensuring every interaction remains respectful and strictly aligned with the safety plan and court-ordered reunification goals.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_implementation.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_implementation.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_implementation.png-wvc-sizes",
  alt: "Professional supervisor facilitating a calm and structured parenting time session"
}, {
  number: "05.",
  title: "Service Scheduling",
  description: "We maintain transparency through regular, objective observation reports. Reporting frequency is typically determined by court requirements or the service agreement, with safety follow-ups conducted as needed. For court-ordered referrals, reports are submitted directly to legal professionals, while family-initiated cases receive documentation focused on progress and developmental milestones.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_reporting.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_reporting.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_reporting.png-wvc-sizes",
  alt: "Documenting objective observations in a professional, neutral report for court review"
}];
const preparationItems = [{
  id: 1,
  text: "Current Court Orders or Legal Agreements"
}, {
  id: 2,
  text: "Contact information for all parties involved"
}, {
  id: 3,
  text: "Identification for participating adults"
}, {
  id: 4,
  text: "Case worker or attorney contact details (if applicable)"
}, {
  id: 5,
  text: "A clear understanding of your requested schedule"
}];
function StepRow({
  step,
  index
}: {
  step: ProcessStep;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const delay = `${index * 200}ms`;
  return <div ref={ref} data-index={index} className="border-t border-border last:border-b">
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-8 md:gap-12 items-center px-4 md:px-8 py-12 md:py-16 md:min-h-[240px]">
        <div className="transition-all duration-700 ease-out group" style={{
        transitionDelay: delay,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)"
      }}>
          <span className="font-default italic text-6xl md:text-[5rem] leading-none text-muted-foreground/60 hover:text-primary transition-colors duration-500" style={{
          WebkitTextStroke: "1px currentColor",
          color: "transparent"
        }}>
            {step.number}
          </span>
        </div>

        <div className="md:max-w-md transition-all duration-700 ease-out" style={{
        transitionDelay: delay,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <h3 className="font-default font-semibold text-3xl md:text-[2rem] leading-tight text-foreground mb-3">
            {step.title}
          </h3>
          <p className="font-default text-muted-foreground text-base leading-relaxed">
            {step.description}
          </p>
        </div>

        <div className="w-full md:w-72 lg:w-80 transition-all duration-700 ease-out overflow-hidden" style={{
        transitionDelay: `calc(${delay} + 200ms)`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateX(0)" : "translateX(40px)"
      }}>
          <div className="relative aspect-square overflow-hidden rounded-lg">
            <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-700" />
          </div>
        </div>
      </div>
    </div>;
}
export default function TheIntakeProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: faqRef,
    isVisible: faqVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4071" id="starting-the-journey" className="dark bg-background text-foreground py-24 md:py-32 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className="text-center mb-20 md:mb-28 transition-all duration-1000 ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <p className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-primary mb-8">
            § 01 &mdash; The Intake Path
          </p>
          <h2 className="font-default text-4xl md:text-6xl lg:text-[5rem] leading-[1.05] text-foreground max-w-4xl mx-auto">
            Five steps to{" "}
            <span className="italic text-primary">resolute support.</span>
          </h2>
          <p className="font-default text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto mt-8 leading-relaxed">
            Before your intake, review our Service Guidelines to understand safety practices, scheduling, payment requirements, and what we expect from all participants.{" "}
            <Link to="#" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
              Download Service Guidelines
            </Link>
          </p>
        </div>

        <div className="w-full">
          {steps.map((step, i) => <StepRow key={step.number} step={step} index={i} />)}
        </div>

        <div className="mt-20 flex flex-col items-center text-center">
          <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:shadow-lg transition-all duration-300 px-10 py-7 text-lg font-semibold">
            <Link to="begin_intake_form_intake_path">
              Begin Intake Process
            </Link>
          </Button>

          <div className="mt-24 max-w-3xl w-full text-left border-l-4 border-primary pl-8 py-4 bg-muted/30 rounded-lg">
            <p className="font-mono text-xs tracking-widest uppercase text-primary mb-4">
              Preparation Checklist
            </p>
            <h3 className="font-default text-2xl md:text-3xl mb-6">What to have ready</h3>
            <ul className="space-y-4">
              {preparationItems.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3 text-muted-foreground">
                  <span className="text-primary mt-1.5 h-1.5 w-1.5 rounded-lg bg-primary shrink-0" />
                  <span className="text-base md:text-lg leading-relaxed">{item.text}</span>
                </li>)}
            </ul>
          </div>

          <div ref={faqRef} className="mt-20 flex flex-col items-center text-center max-w-2xl transition-all duration-1000 ease-out" style={{
          opacity: faqVisible ? 1 : 0,
          transform: faqVisible ? "translateY(0)" : "translateY(24px)"
        }}>
            <p className="font-default text-muted-foreground text-lg mb-8 leading-relaxed">
              Have more questions? Check our comprehensive FAQ for answers to common questions about scheduling, payment, documentation, and the intake process.
            </p>
            <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:shadow-lg transition-all duration-300 px-8 py-6 text-base font-semibold">
              <Link to="/FrequentlyAskedQuestionsPage">
                Visit our FAQ
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}